import { useQuery } from "@tanstack/react-query"
import type { MediaCover, MediaKind, MediaRef } from "@velizhanin-dev/bot-scenario"
import { motion } from "framer-motion"
import { RefreshCw, Upload, X } from "lucide-react"
import { type DragEvent, useRef, useState } from "react"
import type { MediaSource } from "@/entities/bot-builder"
import { MEDIA_KIND_LABELS } from "@/shared/config/media"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { LoadSwap } from "@/shared/ui/load-swap"
import { Reveal } from "@/shared/ui/reveal"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { mediaKindsFor, useMediaSource, useUpload } from "./lib/use-media-upload"
import { Thumbnail } from "./media-thumbnail"

const megabytes = (size: number) => `${(size / 1024 / 1024).toFixed(1).replace(".", ",")} МБ`

/** Превью из хранилища: фото как картинка, видео первым кадром. */
function Preview({ source, media }: { source: MediaSource; media: MediaRef }) {
	const visual = ["photo", "sticker", "video", "animation", "video_note"].includes(media.kind)
	const url = useQuery({
		queryKey: [...source.cacheKey, media.s3Key],
		queryFn: () => source.previewUrl(media.s3Key as string),
		enabled: !!media.s3Key && visual,
		staleTime: 30 * 60_000,
	})
	const [failed, setFailed] = useState(false)
	const video = media.kind === "video" || media.kind === "animation" || media.kind === "video_note"
	if (!visual || !url.data || failed) {
		return <Thumbnail source={source} media={media} size="size-full" />
	}
	return video ? (
		<video
			src={`${url.data.url}#t=0.5`}
			preload="metadata"
			muted
			playsInline
			onError={() => setFailed(true)}
			className="pointer-events-none size-full object-cover"
		/>
	) : (
		<img
			src={url.data.url}
			alt=""
			onError={() => setFailed(true)}
			className="size-full object-cover"
		/>
	)
}

/**
 * Один файл сообщения: перетащить или выбрать, загрузка напрямую в S3 по presigned URL,
 * превью плиткой и выбор, как Telegram его покажет. Пустое поле, загрузка и готовый файл
 * сменяют друг друга перекрёстно, высота идёт плавно.
 */
export function MediaField({
	scenarioId,
	source: sourceProp,
	media,
	onChange,
}: {
	scenarioId?: string
	/** По умолчанию: медиа сценария scenarioId. */
	source?: MediaSource
	media: MediaRef | undefined
	onChange: (media: MediaRef | undefined) => void
}) {
	const inputRef = useRef<HTMLInputElement>(null)
	const source = useMediaSource(scenarioId, sourceProp)
	const { progress, upload } = useUpload(source)
	const [uploading, setUploading] = useState<string | null>(null)
	const [dragging, setDragging] = useState(false)
	// Подписи под файлом сворачиваются уже после того, как файл убрали: держим последний.
	const lastMedia = useRef(media)
	if (media) lastMedia.current = media
	const shown = lastMedia.current
	const kinds = media ? mediaKindsFor(media.mime) : []
	const shownKinds = shown ? mediaKindsFor(shown.mime) : []

	const take = async (file: File | undefined) => {
		if (!file) return
		setUploading(file.name)
		const uploaded = await upload(file)
		setUploading(null)
		if (!uploaded) return
		onChange(
			uploaded.kind === "video" && media?.cover ? { ...uploaded, cover: media.cover } : uploaded,
		)
	}
	const coverInputRef = useRef<HTMLInputElement>(null)
	const coverUpload = useUpload(source)
	const takeCover = async (file: File | undefined) => {
		if (!file || !media) return
		const uploaded = await coverUpload.upload(file, "photo")
		if (!uploaded?.s3Key) return
		const cover: MediaCover = {
			s3Key: uploaded.s3Key,
			fileName: uploaded.fileName,
			mime: uploaded.mime,
			size: uploaded.size,
		}
		onChange({ ...media, cover })
	}
	const pick = () => inputRef.current?.click()
	const drop = {
		onDragOver: (event: DragEvent) => {
			event.preventDefault()
			setDragging(true)
		},
		onDragLeave: () => setDragging(false),
		onDrop: (event: DragEvent) => {
			event.preventDefault()
			setDragging(false)
			void take(event.dataTransfer.files[0])
		},
	}
	const state =
		progress !== null ? "uploading" : media ? `file-${media.s3Key ?? media.fileName}` : "empty"

	return (
		<div>
			<input
				ref={inputRef}
				type="file"
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0]
					if (inputRef.current) inputRef.current.value = ""
					void take(file)
				}}
			/>
			<LoadSwap loading={false} id={state}>
				{progress !== null ? (
					<div className="flex h-24 flex-col justify-center gap-2.5 rounded-xl border border-border bg-secondary/30 px-4">
						<div className="flex items-baseline justify-between gap-3 text-sm">
							<span className="truncate">{uploading ?? "Загружаем файл"}</span>
							<span className="shrink-0 text-xs tabular-nums text-muted-foreground">
								{Math.round(progress)}%
							</span>
						</div>
						<div className="h-1 overflow-hidden rounded-full bg-secondary">
							<motion.div
								className="h-full rounded-full bg-primary"
								initial={{ width: 0 }}
								animate={{ width: `${progress}%` }}
								transition={TRANSITION.base}
							/>
						</div>
					</div>
				) : media ? (
					<div className="flex gap-3 rounded-xl border border-border p-2" {...drop}>
						<div className="relative aspect-video w-24 shrink-0 self-start overflow-hidden rounded-lg bg-muted ring-1 ring-border sm:w-32">
							<Preview source={source} media={media} />
						</div>
						<div className="flex min-w-0 flex-1 flex-col justify-between gap-2 py-0.5">
							<div className="min-w-0">
								<p className="truncate text-sm font-medium">{media.fileName}</p>
								<p className="text-xs text-muted-foreground">
									{MEDIA_KIND_LABELS[media.kind]} · {megabytes(media.size)}
								</p>
							</div>
							<div className="flex flex-wrap gap-1">
								<button
									type="button"
									onClick={pick}
									className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
								>
									<RefreshCw className="size-3.5" />
									Заменить
								</button>
								<button
									type="button"
									onClick={() => onChange(undefined)}
									className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground transition-colors hover:bg-red-500/15 hover:text-red-300"
								>
									<X className="size-3.5" />
									Убрать
								</button>
							</div>
						</div>
					</div>
				) : (
					<button
						type="button"
						onClick={pick}
						{...drop}
						className={cn(
							"flex h-24 w-full flex-col items-center justify-center gap-1 rounded-xl border border-dashed px-4 text-center transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
							dragging
								? "border-primary bg-primary/10 text-foreground"
								: "border-border text-muted-foreground hover:border-foreground/30 hover:bg-secondary/30 hover:text-foreground",
						)}
					>
						<Upload className="size-4" />
						<span className="text-sm">
							{dragging ? "Отпустите, чтобы загрузить" : "Перетащите файл или выберите"}
						</span>
						<span className="text-xs text-muted-foreground">
							Фото, видео, аудио, документ или голосовое
						</span>
					</button>
				)}
			</LoadSwap>
			<Reveal show={!!media && progress === null && kinds.length > 1} className="pt-3">
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-xs text-muted-foreground">Отправить как</span>
					{shown && shownKinds.length > 1 ? (
						<SegmentedControl
							size="sm"
							aria-label="Как отправить файл"
							options={shownKinds.map((kind) => ({ value: kind, label: MEDIA_KIND_LABELS[kind] }))}
							value={shown.kind}
							onChange={(kind) =>
								kind &&
								media &&
								onChange({
									...media,
									kind: kind as MediaKind,
									cover: kind === "video" ? media.cover : undefined,
								})
							}
						/>
					) : null}
				</div>
			</Reveal>
			<Reveal show={media?.kind === "video" && progress === null} className="pt-3">
				<input
					ref={coverInputRef}
					type="file"
					accept="image/jpeg,image/png,image/webp"
					className="hidden"
					onChange={(event) => {
						const file = event.target.files?.[0]
						if (coverInputRef.current) coverInputRef.current.value = ""
						void takeCover(file)
					}}
				/>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
					<span className="shrink-0 text-xs text-muted-foreground">Обложка</span>
					<LoadSwap
						loading={false}
						id={coverUpload.progress !== null ? "uploading" : media?.cover ? "cover" : "empty"}
					>
						{coverUpload.progress !== null ? (
							<span className="text-xs tabular-nums text-muted-foreground">
								Загружаем {Math.round(coverUpload.progress)}%
							</span>
						) : media?.cover ? (
							<div className="flex flex-wrap items-center gap-2">
								<div className="aspect-video h-10 shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-border">
									<Preview source={source} media={{ ...media.cover, kind: "photo" }} />
								</div>
								<button
									type="button"
									onClick={() => coverInputRef.current?.click()}
									className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
								>
									<RefreshCw className="size-3.5" />
									Заменить
								</button>
								<button
									type="button"
									onClick={() => onChange({ ...media, cover: undefined })}
									className="inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-xs text-muted-foreground transition-colors hover:bg-red-500/15 hover:text-red-300"
								>
									<X className="size-3.5" />
									Убрать
								</button>
							</div>
						) : (
							<button
								type="button"
								onClick={() => coverInputRef.current?.click()}
								className="inline-flex h-7 items-center gap-1.5 rounded-full border border-dashed border-border px-3 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
							>
								<Upload className="size-3.5" />
								Загрузить картинку
							</button>
						)}
					</LoadSwap>
				</div>
				<p className="mt-1.5 text-xs text-muted-foreground">
					Без неё Telegram покажет кадр из видео. JPEG, PNG или WebP до 10 МБ.
				</p>
			</Reveal>
			<Reveal show={media?.kind === "video_note" || media?.kind === "sticker"} className="pt-2">
				<p className="text-xs text-muted-foreground">
					{shown ? MEDIA_KIND_LABELS[shown.kind] : ""} уходит без подписи: текст бот пришлёт
					следующим сообщением.
				</p>
			</Reveal>
		</div>
	)
}
